<script setup lang="ts">
import { computed, ref, watch } from "vue";
import {
  UiButton,
  UiCheckbox,
  UiForm,
  UiFormField,
  UiInputField,
  UiSegmentedControl,
  UiText,
} from "@shopexperts/shared";
import {
  EXPERT_PLAN_TYPE_OPTIONS,
  expertPlanFormSchema,
  suggestSlugFromName,
  type ExpertPlanFormValues,
} from "@/app/composables/expert-plans/expertPlanForm";

const props = defineProps<{
  initialValues: ExpertPlanFormValues;
  isEditing?: boolean;
  isSubmitting?: boolean;
  inline?: boolean;
  showCancel?: boolean;
  expertTypeLocked?: boolean;
  serverErrors?: Record<string, string | undefined>;
}>();

const emit = defineEmits<{
  (e: "submit", value: ExpertPlanFormValues): void;
  (e: "cancel"): void;
  (e: "clear-server-errors"): void;
}>();

// UiForm seeds vee-validate from initialValues (not from the bound model)
// and writes those values back into the model on mount, so the same object
// must be passed as both.
const initialFormValues: ExpertPlanFormValues = { ...props.initialValues };
const formModel = ref<ExpertPlanFormValues>({ ...initialFormValues });
const isDirty = ref(false);
const fieldIdPrefix = computed(() => props.isEditing ? "expert-plan-edit" : "expert-plan-create");

const expertTypeHint = computed(() => {
  if (props.expertTypeLocked) {
    return "Audience is locked because this plan already has subscriptions.";
  }
  if (props.isEditing) {
    return "Controls which experts can receive this plan. It locks after any invitation or subscription references the plan.";
  }
  return "Required. Choose whether this plan is for freelancers, agencies, or all experts.";
});

// Auto-maintain the slug from the name while creating, until the admin
// types their own slug.
watch(
  () => formModel.value?.name,
  (nextName, prevName) => {
    if (props.isEditing || !formModel.value) return;
    const trimmed = (nextName ?? "").trim();
    if (!trimmed) return;

    const currentSlug = (formModel.value.slug ?? "").trim();
    const previousSuggestion = suggestSlugFromName(prevName ?? "");
    if (currentSlug !== "" && currentSlug !== previousSuggestion) return;

    const suggested = suggestSlugFromName(trimmed);
    if (suggested && suggested !== currentSlug) {
      formModel.value = { ...formModel.value, slug: suggested };
    }
  },
);

watch(formModel, () => {
  if (props.serverErrors && Object.keys(props.serverErrors).length > 0) {
    emit("clear-server-errors");
  }
}, { deep: true });

function onSubmit(values: ExpertPlanFormValues) {
  emit("submit", values);
}
</script>

<template>
  <UiForm
    v-model="formModel"
    v-model:dirty="isDirty"
    :schema="expertPlanFormSchema"
    :initial-values="initialFormValues"
    :server-errors="props.serverErrors"
    scroll-to-first-error
    @submit="onSubmit"
  >
    <div class="flex flex-col gap-5" :class="props.inline ? 'max-w-none' : 'max-w-xl'">
      <div class="grid grid-cols-1 gap-5">
        <UiFormField
          name="name"
          :id="`${fieldIdPrefix}-name`"
          label="Plan name"
          hint="Shown to admins and used in the Stripe product name."
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error, disabled }">
            <UiInputField
              :id="id"
              name="name"
              placeholder="e.g., Freelancer Pro"
              :model-value="(modelValue as string)"
              :invalid="invalid"
              :error="error"
              :disabled="disabled"
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
            />
          </template>
        </UiFormField>

        <UiFormField
          name="expert_type"
          :id="`${fieldIdPrefix}-expert-type`"
          label="Available to"
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error, disabled }">
            <UiSegmentedControl
              :id="id"
              name="expert_type"
              :options="[...EXPERT_PLAN_TYPE_OPTIONS]"
              :model-value="(modelValue as string)"
              :disabled="disabled || props.expertTypeLocked || props.isSubmitting"
              aria-label="Available to"
              :aria-describedby="`${id}-${invalid ? 'error' : 'hint'}`"
              :invalid="invalid"
              @update:model-value="(value) => { onUpdateModelValue(value); if (!error) onValidate(); }"
            />
            <UiText
              v-if="error || expertTypeHint"
              as="span"
              variant="caption-normal"
              class="mt-1"
              :class="invalid ? 'text-se-danger' : 'text-se-gray-700'"
              :id="`${id}-${invalid ? 'error' : 'hint'}`"
              :text="error || expertTypeHint"
            />
          </template>
        </UiFormField>

        <UiFormField
          v-if="props.isEditing !== true"
          name="slug"
          :id="`${fieldIdPrefix}-slug`"
          label="Slug"
          hint="Lowercase letters, numbers and hyphens. Suggested from the plan name."
        >
          <template #default="{ modelValue, onUpdateModelValue, onValidate, id, invalid, error }">
            <UiInputField
              :id="id"
              name="slug"
              placeholder="freelancer-pro"
              :model-value="(modelValue as string)"
              :invalid="invalid"
              :error="error"
              @update:model-value="onUpdateModelValue"
              @validate="onValidate"
            />
          </template>
        </UiFormField>
      </div>

      <div class="grid grid-cols-1 gap-4" :class="props.inline ? 'xl:grid-cols-2' : ''">
        <UiFormField name="is_active" :id="`${fieldIdPrefix}-is-active`" type="checkbox">
          <template #default="{ modelValue, onUpdateModelValue, id }">
            <UiCheckbox
              :id="id"
              name="is_active"
              label="Plan is active in the subscription catalog"
              :model-value="Boolean(modelValue)"
              @update:model-value="onUpdateModelValue"
            />
          </template>
        </UiFormField>

        <UiFormField name="is_founding" :id="`${fieldIdPrefix}-is-founding`" type="checkbox">
          <template #default="{ modelValue, onUpdateModelValue, id }">
            <UiCheckbox
              :id="id"
              name="is_founding"
              label="Tag as a founding-member plan"
              :model-value="Boolean(modelValue)"
              @update:model-value="onUpdateModelValue"
            />
          </template>
        </UiFormField>
      </div>

      <div class="flex flex-col-reverse sm:flex-row sm:justify-end gap-3 pt-1">
        <UiButton
          v-if="props.showCancel !== false"
          variant="outline"
          label="Cancel"
          :disabled="props.isSubmitting"
          @click="emit('cancel')"
        />
        <UiButton
          type="submit"
          variant="primary"
          :label="props.isSubmitting ? 'Saving...' : props.isEditing ? 'Save plan' : 'Create plan'"
          :disabled="props.isSubmitting || (props.isEditing === true && !isDirty)"
        />
      </div>
    </div>
  </UiForm>
</template>
